import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { AddIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import { useRouter } from 'next/router';
import * as React from 'react';
import { isFreeUser } from 'src/constants/plans';
import { useBoardStore } from 'src/features/boards';
import { useAuth } from 'src/shared/hooks/use-auth';
import { shallow } from 'zustand/shallow';

import { useKanbanStore } from '../../store';
import { KanbanState } from '../../store/types';
import { TemplateSelect } from './TemplateSelect';
import { ColumnToolbar } from './Toolbar';
const { useMemo } = React;
export interface KanbanColumnHeaderProps {
  data: {
    id: string;
    name: string;
    color: string;
    count: number;
    index: number;
    onAddNewTask: (
      columnId: KanbanState['currentSelectedItem']['columnId'],
      index: KanbanState['currentSelectedItem']['itemIndex'],
      mode: KanbanState['currentSelectedItem']['mode'],
      top: boolean,
      selectedTemplateRef?: string
    ) => void;
  };
}

const { useState, useEffect } = React;

export const KanbanColumnHeader = ({
  data: { name, color, id, count, index, onAddNewTask },
}: KanbanColumnHeaderProps) => {
  const [isEditColumnTitle, setIsEditColumnTitle] = useState(false);
  const [changeName, setChangeName] = useState('');
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const router = useRouter();

  const { onChangeColumnName, onChangeColumnColor, onDeleteColumn, isReadOnly } = useKanbanStore(
    stateSelector,
    shallow
  );

  const authData = useAuth((s) => s.userDetails);
  const currentPlan = authData?.plan?.type ?? '';
  const { access, totalTaskCount } = useBoardStore((s) => ({
    access: s.access,
    totalTaskCount: s.totalTaskCount,
  }));
  const canAddTask = useMemo(
    () => !isFreeUser(currentPlan) || totalTaskCount < 10,
    [totalTaskCount]
  );
  useEffect(() => {
    setChangeName(name);
  }, [name]);

  const handleColumnChange = (e) => {
    if (e.value === 'delete') {
      setIsDeletePromptOpen(true);
    } else if (e.value != 'colour-picker') {
      onChangeColumnColor({ id: id, color: e.value });
    }
  };

  const handleAddTask = (selectedTemplate?: string) => {
    onAddNewTask(id, undefined, 'add', true, selectedTemplate);
    // onAddNewTask(id, undefined, 'add', true);
  };

  return (
    <div>
      <div className="w-284 mr-16 py-10 mb-5">
        <div className="flex items-center justify-between">
          {!isReadOnly && isEditColumnTitle && access !== 'view' ? (
            <input
              className="border border-gray-200 rounded-md px-9 py-6 bg-white"
              autoFocus
              onChange={(e) => {
                setChangeName(e.target.value);
              }}
              value={changeName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onChangeColumnName({ id: id, name: changeName });
                  setIsEditColumnTitle(false);
                }
              }}
              onBlur={() => {
                onChangeColumnName({ id: id, name: changeName });
                setIsEditColumnTitle(false);
              }}
              disabled={router?.query?.type === 'agile'}
            />
          ) : (
            <span
              className="px-8 py-1 rounded-sm"
              style={{ backgroundColor: color }}
              onDoubleClick={() => {
                if (router?.query?.type !== 'agile') setIsEditColumnTitle(true);
              }}
            >
              {name} ({count})
            </span>
          )}
          {router?.query?.type !== 'agile' && (
            <div className="flex">
              <div className="mr-4 mt-6">
                {!isReadOnly && access !== 'view' && canAddTask ? (
                  <button className="flex px-8 py-8" tabIndex={-1} onClick={() => handleAddTask()}>
                    <AddIcon wrapperClass="addtask text-gray-500" />
                  </button>
                ) : // <Tippy content={<span>Create new task</span>}>
                //   <Popover>
                //     <PopoverTrigger>
                //       <button className="flex px-8 py-8" tabIndex={-1}>
                //         <AddIcon wrapperClass="addtask text-gray-500" />
                //       </button>
                //     </PopoverTrigger>
                //     <PopoverContent>
                //       <TemplateSelect addnewTask={handleAddTask} />
                //     </PopoverContent>
                //   </Popover>
                // </Tippy>
                !canAddTask ? (
                  <Tippy content={<span>Upgrade to unlock</span>}>
                    <button className="flex" tabIndex={-1}>
                      <AddIcon wrapperClass="addtask text-gray-400 text-sm mt-8" />
                    </button>
                  </Tippy>
                ) : (
                  <></>
                )}
              </div>

              <div className="text-gray-500  mt-12">
                {!isReadOnly && access !== 'view' ? (
                  <>
                    <ColumnToolbar onClick={handleColumnChange} />
                  </>
                ) : null}
              </div>
              <Modal
                isOpen={isDeletePromptOpen}
                onClose={() => {
                  setIsDeletePromptOpen(false);
                }}
              >
                <ModalOverlay />

                <ModalContent
                  style={{
                    maxWidth: '46rem',
                    padding: '32px',
                    marginTop: '20rem',
                    borderRadius: '8px',
                  }}
                >
                  <ModalCloseButton />
                  <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setIsDeletePromptOpen(false);
                      }}
                      className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
                    >
                      Cancel
                    </button>
                    <button
                      className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                      onClick={() => onDeleteColumn({ columnId: id })}
                    >
                      Delete
                    </button>
                  </div>
                </ModalContent>
              </Modal>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const stateSelector = (s: KanbanState) => ({
  onChangeColumnName: s.onChangeColumnName,
  onChangeColumnColor: s.onChangeColumnColor,
  onDeleteColumn: s.onDeleteColumn,
  isReadOnly: s.isReadOnly,
});
